import {
    AddOutlined,
    ArrowBackOutlined,
    AttachFileOutlined,
    CloseOutlined,
    DownloadOutlined,
    DriveFileRenameOutlineOutlined,
    FullscreenOutlined,
    GridOnOutlined,
    HideImageOutlined,
    PictureAsPdfOutlined,
    PrintOutlined,
    SaveOutlined,
} from "@mui/icons-material";
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardActions,
    Chip,
    CircularProgress,
    Fab,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    LocalizationProvider,
    MobileDateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
    ChangeEvent,
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useCookies } from "react-cookie";
import expenseExportById from "../../../../resources/Api/vehicleexpense/expenseExportById";
import expenseSaveByVehicle from "../../../../resources/Api/vehicleexpense/expenseSaveByVehicle";
import expensesFindAllByActivated from "../../../../resources/Api/vehicleexpense/expensesFindAllByActivated";
import expenseTypesFindAllByActivated from "../../../../resources/Api/vehicleexpense/expenseTypesFindAllByActivated";
import expenseUpdateByVehicle from "../../../../resources/Api/vehicleexpense/expenseUpdateByVehicle";
import { currencyFormatter } from "../../../../functions/functions";
import {
    ExpenseFileItem,
    ExpenseRequest,
    ExpenseResponse,
    ExpenseType,
    ExpenseView,
} from "../../../../types/types";

type StatusMessage = "warn" | "success" | "info" | "error" | undefined;

type VehicleExpenseProps = {
    vehicleId: number;
    customerId: number;
    isDisabled: boolean;
    setTextValidation: Dispatch<SetStateAction<string>>;
    setStatusMessage: Dispatch<SetStateAction<StatusMessage>>;
    setFieldsIsValid: Dispatch<SetStateAction<boolean>>;
    requestCancelConfirmation: (onConfirm: () => void) => void;
    onUnsavedChangesChange: (hasUnsavedChanges: boolean) => void;
};

type AttachmentPreviewProps = {
    fileUrl: string;
    fileName: string;
    onDownload: () => void;
    onFullscreen: () => void;
    onRemove?: () => void;
};

const PAGE_SIZE = 8;
const MAX_ATTACHMENTS = 3;

const INITIAL_EXPENSE: ExpenseRequest = {
    vehicleId: -1,
    customerId: -1,
    expenseTypeId: -1,
    expenseDate: null,
    description: null,
    quantity: null,
    totalValue: null,
    files: [],
    removedFiles: [],
};

const exportItems = [
    { label: "PDF", icon: <PictureAsPdfOutlined /> },
    { label: "Excel", icon: <GridOnOutlined /> },
    { label: "Excel simples", icon: <GridOnOutlined /> },
];

const formatDateTime = (value?: string | null): string => {
    if (!value) {
        return "-";
    }

    const brazilianDateTime = value.match(
        /^(\d{2}\/\d{2}\/\d{4})[ T](\d{2}:\d{2})(?::\d{2})?/,
    );

    if (brazilianDateTime) {
        return `${brazilianDateTime[1]} ${brazilianDateTime[2]}`;
    }

    const parsedDate = dayjs(value);
    return parsedDate.isValid() ? parsedDate.format("DD/MM/YYYY HH:mm") : value;
};

const getApiErrorMessage = (error: any, fallback: string): string => {
    const reason = error?.response?.data?.reason;

    if (Array.isArray(reason)) {
        return reason.filter(Boolean).join(". ") || fallback;
    }

    return typeof reason === "string" && reason.trim() ? reason : fallback;
};

const AttachmentPreview = ({
    fileUrl,
    fileName,
    onDownload,
    onFullscreen,
    onRemove,
}: AttachmentPreviewProps) => (
    <Card
        sx={{
            background: "#f3eeeec4",
            padding: 0.5,
            margin: 0.5,
            borderRadius: 1,
        }}
    >
        <Box
            component="img"
            src={fileUrl}
            alt={fileName}
            sx={{
                objectFit: "cover",
                width: 150,
                height: 150,
                display: "block",
                borderRadius: 1,
                background: "gray",
                margin: 0.5,
            }}
        />

        <CardActions sx={{ justifyContent: "center" }}>
            <Tooltip title="Baixar anexo">
                <IconButton size="small" onClick={onDownload} aria-label="Baixar anexo">
                    <DownloadOutlined sx={{ color: "#00B700" }} />
                </IconButton>
            </Tooltip>

            {onRemove && (
                <Tooltip title="Remover anexo">
                    <IconButton size="small" onClick={onRemove} aria-label="Remover anexo">
                        <HideImageOutlined sx={{ color: "red" }} />
                    </IconButton>
                </Tooltip>
            )}

            <Tooltip title="Visualizar em tela cheia">
                <IconButton
                    size="small"
                    onClick={onFullscreen}
                    aria-label="Visualizar anexo em tela cheia"
                >
                    <FullscreenOutlined sx={{ color: "blue" }} />
                </IconButton>
            </Tooltip>
        </CardActions>
    </Card>
);

const VehicleExpense = ({
    vehicleId,
    customerId,
    isDisabled,
    setTextValidation,
    setStatusMessage,
    setFieldsIsValid,
    requestCancelConfirmation,
    onUnsavedChangesChange,
}: VehicleExpenseProps) => {
    const [cookies] = useCookies(["token"]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [expenseView, setExpenseView] = useState<ExpenseView>("list");
    const [expense, setExpense] = useState<ExpenseRequest>({ ...INITIAL_EXPENSE });
    const [selectedExpense, setSelectedExpense] =
        useState<ExpenseResponse | null>(null);
    const [expenseList, setExpenseList] = useState<ExpenseResponse[]>([]);
    const [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>([]);
    const [newFiles, setNewFiles] = useState<ExpenseFileItem[]>([]);
    const [removedFiles, setRemovedFiles] = useState<number[]>([]);

    const [page, setPage] = useState(0);
    const [lastPage, setLastPage] = useState(true);
    const [totalElements, setTotalElements] = useState(0);
    const [listLoading, setListLoading] = useState(false);
    const [typesLoading, setTypesLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [exportLoadingId, setExportLoadingId] = useState<number | null>(null);
    const [exportAnchorEl, setExportAnchorEl] = useState<HTMLElement | null>(null);
    const [exportExpenseId, setExportExpenseId] = useState<number | null>(null);

    const showMessage = useCallback(
        (message: string, status: StatusMessage) => {
            setTextValidation(message);
            setStatusMessage(status);
            setFieldsIsValid(false);
        },
        [setFieldsIsValid, setStatusMessage, setTextValidation],
    );

    const loadExpenseTypes = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        setTypesLoading(true);

        try {
            const response = await expenseTypesFindAllByActivated(token, "activated");
            setExpenseTypes(response.data ?? []);
        } catch (error) {
            console.error("Erro ao buscar tipos de despesas:", error);
            setExpenseTypes([]);
            showMessage("Não foi possível carregar os tipos de despesas.", "error");
        } finally {
            setTypesLoading(false);
        }
    }, [cookies.token, showMessage]);

    const loadExpenses = useCallback(
        async (targetPage = 0) => {
            const token: string = cookies.token || sessionStorage.getItem("token");

            if (vehicleId <= 0 || customerId <= 0) {
                setExpenseList([]);
                setPage(0);
                setLastPage(true);
                setTotalElements(0);
                return;
            }

            setListLoading(true);
            try {
                const response = await expensesFindAllByActivated(
                    token,
                    customerId,
                    vehicleId,
                    "activated",
                    targetPage,
                    PAGE_SIZE,
                );
                const data = response.data;

                setExpenseList(data?.content ?? []);
                setPage(data?.number ?? data?.pageable?.pageNumber ?? targetPage);
                setLastPage(data?.last ?? true);
                setTotalElements(data?.totalElements ?? 0);
            } catch (error) {
                console.error("Erro ao buscar despesas:", error);
                setExpenseList([]);
                setPage(0);
                setLastPage(true);
                setTotalElements(0);
                showMessage("Não foi possível carregar as despesas.", "error");
            } finally {
                setListLoading(false);
            }
        },
        [cookies.token, customerId, showMessage, vehicleId],
    );

    useEffect(() => {
        if (vehicleId <= 0 || customerId <= 0) {
            return;
        }

        setExpenseView("list");
        setSelectedExpense(null);
        setPage(0);
        void Promise.all([loadExpenseTypes(), loadExpenses(0)]);
    }, [customerId, loadExpenseTypes, loadExpenses, vehicleId]);

    const resetExpenseForm = useCallback(() => {
        setExpense({ ...INITIAL_EXPENSE });
        setSelectedExpense(null);
        setNewFiles([]);
        setRemovedFiles([]);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }, []);

    const handleBackToList = useCallback(() => {
        resetExpenseForm();
        setExpenseView("list");
        onUnsavedChangesChange(false);
    }, [onUnsavedChangesChange, resetExpenseForm]);

    const handleNewExpense = useCallback(() => {
        resetExpenseForm();
        setExpense({
            ...INITIAL_EXPENSE,
            vehicleId,
            customerId,
        });
        setExpenseView("form");
    }, [customerId, resetExpenseForm, vehicleId]);

    const handleEditExpense = useCallback(
        (item: ExpenseResponse) => {
            resetExpenseForm();
            setSelectedExpense(item);
            setExpense({
                id: item.id,
                vehicleId: item.vehicleId,
                customerId,
                expenseTypeId: item.expenseTypeId,
                expenseDate: item.expenseDate,
                description: item.description ?? null,
                quantity: item.quantity != null ? Number(item.quantity) : null,
                totalValue: item.totalValue != null ? Number(item.totalValue) : null,
                files: [],
                removedFiles: [],
            });
            setExpenseView("form");
        },
        [customerId, resetExpenseForm],
    );

    const hasExpenseChanges = useCallback(() => {
        const normalizeString = (value?: string | null) => value?.trim() ?? "";
        const normalizeNumber = (value?: number | null) =>
            value == null || Number.isNaN(Number(value)) ? null : Number(value);
        const normalizeDateTime = (value?: string | null) =>
            value && dayjs(value).isValid()
                ? dayjs(value).format("YYYY-MM-DDTHH:mm:ss")
                : "";
        const attachmentChanged = newFiles.length > 0 || removedFiles.length > 0;

        if (!selectedExpense) {
            return (
                expense.expenseTypeId > 0 ||
                Boolean(normalizeDateTime(expense.expenseDate)) ||
                Boolean(normalizeString(expense.description)) ||
                expense.quantity != null ||
                expense.totalValue != null ||
                attachmentChanged
            );
        }

        return (
            expense.expenseTypeId !== selectedExpense.expenseTypeId ||
            normalizeDateTime(expense.expenseDate) !==
            normalizeDateTime(selectedExpense.expenseDate) ||
            normalizeString(expense.description) !==
            normalizeString(selectedExpense.description) ||
            normalizeNumber(expense.quantity) !==
            normalizeNumber(selectedExpense.quantity) ||
            normalizeNumber(expense.totalValue) !==
            normalizeNumber(selectedExpense.totalValue) ||
            attachmentChanged
        );
    }, [expense, newFiles.length, removedFiles.length, selectedExpense]);

    useEffect(() => {
        onUnsavedChangesChange(expenseView === "form" && hasExpenseChanges());
    }, [expenseView, hasExpenseChanges, onUnsavedChangesChange]);

    useEffect(
        () => () => onUnsavedChangesChange(false),
        [onUnsavedChangesChange],
    );

    const verifyBeforeCancel = useCallback(() => {
        if (hasExpenseChanges()) {
            requestCancelConfirmation(handleBackToList);
            return;
        }

        handleBackToList();
    }, [handleBackToList, hasExpenseChanges, requestCancelConfirmation]);

    const handleSaveExpense = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const quantity = Number(expense.quantity);
        const totalValue = Number(expense.totalValue);

        if (vehicleId <= 0) {
            showMessage("Veículo inválido.", "warn");
            return;
        }

        if (customerId <= 0) {
            showMessage("Cliente inválido.", "warn");
            return;
        }

        if (expense.expenseTypeId <= 0) {
            showMessage("Informe o tipo da despesa.", "warn");
            return;
        }

        if (!expense.expenseDate || !dayjs(expense.expenseDate).isValid()) {
            showMessage("Informe a data e hora da despesa.", "warn");
            return;
        }

        if (
            expense.quantity == null ||
            !Number.isFinite(quantity) ||
            quantity <= 0
        ) {
            showMessage("Informe uma quantidade maior que zero.", "warn");
            return;
        }

        if (
            expense.totalValue == null ||
            !Number.isFinite(totalValue) ||
            totalValue <= 0
        ) {
            showMessage("Informe um valor total maior que zero.", "warn");
            return;
        }

        if ((expense.description?.trim().length ?? 0) > 250) {
            showMessage("A descrição deve ter no máximo 250 caracteres.", "warn");
            return;
        }

        const isUpdate = selectedExpense != null && selectedExpense.id > 0;

        if (isUpdate && !hasExpenseChanges()) {
            showMessage("Nada foi alterado.", "warn");
            return;
        }

        const data: ExpenseRequest = {
            ...expense,
            vehicleId,
            customerId,
            expenseDate: dayjs(expense.expenseDate).format(
                "YYYY-MM-DDTHH:mm:ss",
            ),
            description: expense.description?.trim() || null,
            quantity,
            totalValue,
            files: newFiles.map((item) => item.file),
            removedFiles,
        };

        setSaving(true);

        try {
            if (isUpdate) {
                await expenseUpdateByVehicle(token, selectedExpense.id, data);
            } else {
                await expenseSaveByVehicle(token, data);
            }

            showMessage(
                isUpdate
                    ? "Despesa atualizada com sucesso."
                    : "Despesa salva com sucesso.",
                "success",
            );
            resetExpenseForm();
            setExpenseView("list");
            setPage(0);
            onUnsavedChangesChange(false);
            await loadExpenses(0);
        } catch (error: any) {
            console.error(
                isUpdate ? "Erro ao atualizar despesa:" : "Erro ao salvar despesa:",
                error,
            );
            showMessage(
                getApiErrorMessage(
                    error,
                    isUpdate
                        ? "Erro ao atualizar a despesa."
                        : "Erro ao salvar a despesa.",
                ),
                error?.response?.data?.reason ? "warn" : "error",
            );
        } finally {
            setSaving(false);
        }
    }, [
        cookies.token,
        customerId,
        expense,
        hasExpenseChanges,
        loadExpenses,
        newFiles,
        onUnsavedChangesChange,
        removedFiles,
        resetExpenseForm,
        selectedExpense,
        showMessage,
        vehicleId,
    ]);

    const handleFileSelection = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const selectedFile = event.target.files?.[0];

            if (!selectedFile || !selectedFile.type.startsWith("image/")) {
                showMessage("Selecione um arquivo de imagem válido.", "warn");
                event.target.value = "";
                return;
            }

            const reader = new FileReader();
            reader.readAsDataURL(selectedFile);
            reader.onloadend = () => {
                if (typeof reader.result !== "string") {
                    showMessage("Não foi possível ler o anexo selecionado.", "error");
                    return;
                }

                setNewFiles((previous) => [
                    ...previous,
                    {
                        id: Date.now() + Math.random(),
                        file: reader.result as string,
                        type: selectedFile.type.substring(0, 5),
                        name: selectedFile.name,
                    },
                ]);

                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            };
        },
        [showMessage],
    );

    const handleFileDownload = useCallback((fileUrl: string, fileName: string) => {
        const link = document.createElement("a");
        link.href = fileUrl;

        let extension = "";
        if (fileUrl.startsWith("data:")) {
            const mime = fileUrl.split(";")[0].split(":")[1];
            if (mime?.includes("/")) {
                extension = `.${mime.split("/")[1]}`;
            }
        }

        link.download = fileName.includes(".")
            ? fileName
            : `${fileName || "anexo"}${extension || ".png"}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, []);

    const handleFileFullscreen = useCallback((fileUrl: string, fileName: string) => {
        if (document.fullscreenElement) {
            void document.exitFullscreen();
            return;
        }

        const element = document.createElement("img");
        element.className = "expense-img-anexo";
        element.id = fileName;
        element.src = fileUrl;
        element.style.objectFit = "contain";
        element.style.width = "100%";
        element.style.height = "100%";
        document.body.appendChild(element);

        void element.requestFullscreen().catch((error) => {
            console.error("Erro ao abrir anexo em tela cheia:", error);
            if (document.body.contains(element)) {
                document.body.removeChild(element);
            }
        });

        element.addEventListener("fullscreenchange", () => {
            if (!document.fullscreenElement && document.body.contains(element)) {
                document.body.removeChild(element);
            }
        });
    }, []);

    const visibleExistingFiles = useMemo(
        () =>
            selectedExpense?.files?.filter(
                (file) => file.activated !== false && !removedFiles.includes(file.id),
            ) ?? [],
        [removedFiles, selectedExpense?.files],
    );

    const attachmentsCount = visibleExistingFiles.length + newFiles.length;
    const handleExport = useCallback(
        async (id: number, selectedType: string) => {
            const token: string = cookies.token || sessionStorage.getItem("token");
            const simpleXlsxExport = selectedType.includes("simples");
            const reportType = selectedType.split(" ")[0];
            setExportLoadingId(id);

            try {
                const response = await expenseExportById(
                    token,
                    id,
                    reportType,
                    simpleXlsxExport,
                );
                const data = response.data as ArrayBuffer;

                if (!data || data.byteLength === 0) {
                    showMessage("Nenhum dado para mostrar no relatório.", "warn");
                    return;
                }

                const isPdf = reportType === "pdf";
                const blob = new Blob([data], {
                    type: isPdf
                        ? "application/pdf"
                        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });
                const fileUrl = URL.createObjectURL(blob);

                if (isPdf) {
                    window.open(fileUrl, "_blank", "noopener,noreferrer");
                } else {
                    const link = document.createElement("a");
                    link.href = fileUrl;
                    link.download = `despesa-${id}.xlsx`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }

                window.setTimeout(() => URL.revokeObjectURL(fileUrl), 60000);
                showMessage("Relatório gerado com sucesso.", "success");
            } catch (error: any) {
                const responseData = error?.response?.data;
                let reason = "Erro ao gerar o relatório.";

                if (responseData instanceof ArrayBuffer) {
                    try {
                        const decodedError = new TextDecoder().decode(responseData);
                        reason = JSON.parse(decodedError)?.reason ?? reason;
                    } catch {
                        // Mantém a mensagem padrão quando o backend não devolve JSON.
                    }
                } else if (responseData?.reason) {
                    reason = responseData.reason;
                }

                showMessage(reason, "warn");
            } finally {
                setExportLoadingId(null);
            }
        },
        [cookies.token, showMessage],
    );

    const renderAttachment = useCallback(
        (
            fileUrl: string,
            fileName: string,
            key: string | number,
            onRemove?: () => void,
        ) => (
            <AttachmentPreview
                key={key}
                fileUrl={fileUrl}
                fileName={fileName}
                onDownload={() => handleFileDownload(fileUrl, fileName)}
                onFullscreen={() => handleFileFullscreen(fileUrl, fileName)}
                onRemove={onRemove}
            />
        ),
        [handleFileDownload, handleFileFullscreen],
    );

    return (
        <Paper
            className="tw-rounded-xl tw-border tw-border-black/10 tw-bg-white tw-p-4 sm:tw-p-5"
            elevation={0}
            sx={{
                background: "#fff",
                padding: { xs: 2, sm: 2.5 },
                border: "1px solid rgba(0, 63, 74, 0.10)",
                borderRadius: "12px",
                "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#ffff", transition: "box-shadow 200ms ease, background-color 200ms ease" },
                "& .MuiOutlinedInput-root:hover": { backgroundColor: "rgba(0, 108, 119, 0.02)" },
                "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 0 3px rgba(0, 108, 119, 0.10)" },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#006C77" },
            }}
        >
            {expenseView === "list" && (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 2,
                        }}
                    >
                        <Typography sx={{ fontFamily: "Prometo", fontWeight: "bold" }}>
                            Histórico de despesas
                        </Typography>

                        <Tooltip title="Criar nova despesa">
                            <span>
                                <Fab
                                    size="small"
                                    aria-label="Criar nova despesa"
                                    onClick={handleNewExpense}
                                    disabled={isDisabled || listLoading}
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        minHeight: 40,
                                        backgroundColor: "#00B700",
                                        color: "#fff",
                                        boxShadow: "none",
                                        "&:hover": {
                                            backgroundColor: "#009900",
                                            boxShadow: "none",
                                        },
                                    }}
                                >
                                    <AddOutlined />
                                </Fab>
                            </span>
                        </Tooltip>
                    </Box>

                    <TableContainer
                        className="tw-overflow-hidden tw-rounded-xl tw-border-black/10"
                        component={Paper}
                        sx={{
                            maxHeight: 500,
                            boxShadow: "none",
                            border: "1px solid #e0e0e0",
                            borderRadius: 3,
                        }}
                    >
                        <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
                            <TableHead>
                                <TableRow>
                                    {[
                                        "Data da despesa",
                                        "Tipo",
                                        "Descrição",
                                        "Quantidade",
                                        "Valor total",
                                        "Ações",
                                    ].map((title) => (
                                        <TableCell
                                            key={title}
                                            sx={{
                                                fontWeight: "bold",
                                                fontFamily: "Prometo",
                                                background: "#09414A",
                                                color: "#fff",
                                            }}
                                        >
                                            {title}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {listLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ padding: 4 }}>
                                            <Stack
                                                direction="row"
                                                spacing={1.5}
                                                alignItems="center"
                                                justifyContent="center"
                                                role="status"
                                                aria-live="polite"
                                            >
                                                <CircularProgress size={24} sx={{ color: "#00B700" }} />
                                                <Typography sx={{ fontFamily: "Prometo" }}>
                                                    Carregando despesas...
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ) : expenseList.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={6}
                                            align="center"
                                            sx={{ padding: 4, fontFamily: "Prometo" }}
                                        >
                                            Nenhuma despesa encontrada.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    expenseList.map((item) => (
                                        <TableRow key={item.id} hover>
                                            <TableCell>
                                                {item.expenseDate
                                                    ? dayjs(item.expenseDate).format(
                                                        "DD/MM/YYYY HH:mm:ss",
                                                    )
                                                    : "-"}
                                            </TableCell>
                                            <TableCell>{item.expenseTypeName || "-"}</TableCell>
                                            <TableCell
                                                title={item.description || undefined}
                                                sx={{ maxWidth: 280 }}
                                            >
                                                <Typography noWrap variant="body2">
                                                    {item.description || "-"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {Number(item.quantity).toLocaleString("pt-BR")}
                                            </TableCell>
                                            <TableCell>
                                                {Number(item.totalValue).toLocaleString("pt-BR", {
                                                    style: "currency",
                                                    currency: "BRL",
                                                })}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                <Tooltip title="Editar despesa">
                                                    <span>
                                                        <IconButton
                                                            disabled={isDisabled}
                                                            onClick={() => handleEditExpense(item)}
                                                            aria-label="Editar despesa"
                                                        >
                                                            <DriveFileRenameOutlineOutlined />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>

                                                <Tooltip title="Anexos da despesa">
                                                    <IconButton
                                                        onClick={() => {
                                                            setSelectedExpense(item);
                                                            setExpenseView("attachments");
                                                        }}
                                                        aria-label="Anexos da despesa"
                                                    >
                                                        <AttachFileOutlined />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="Exportar despesa">
                                                    <IconButton
                                                        aria-label="Exportar despesa"
                                                        disabled={exportLoadingId != null}
                                                        onClick={(event) => {
                                                            setExportExpenseId(item.id);
                                                            setExportAnchorEl(event.currentTarget);
                                                        }}
                                                    >
                                                        {exportLoadingId === item.id ? (
                                                            <CircularProgress
                                                                size={22}
                                                                sx={{ color: "#00B700" }}
                                                            />
                                                        ) : (
                                                            <PrintOutlined />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Menu
                        open={Boolean(exportAnchorEl)}
                        anchorEl={exportAnchorEl}
                        onClose={() => setExportAnchorEl(null)}
                    >
                        {exportItems.map((option) => (
                            <MenuItem
                                key={option.label}
                                onClick={() => {
                                    if (exportExpenseId) {
                                        void handleExport(
                                            exportExpenseId,
                                            option.label.toLocaleLowerCase(),
                                        );
                                    }
                                    setExportAnchorEl(null);
                                }}
                            >
                                {option.icon}
                                <Typography sx={{ marginLeft: 1, fontFamily: "Prometo" }}>
                                    {option.label}
                                </Typography>
                            </MenuItem>
                        ))}
                    </Menu>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                            marginTop: 2,
                        }}
                    >
                        <Typography variant="body2" sx={{ fontFamily: "Prometo", color: "gray" }}>
                            Total: {totalElements}
                        </Typography>

                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                                type="button"
                                variant="outlined"
                                disabled={listLoading || page <= 0}
                                onClick={() => void loadExpenses(page - 1)}
                                sx={{ fontFamily: "Prometo", textTransform: "none" }}
                            >
                                Anterior
                            </Button>
                            <Typography sx={{ fontFamily: "Prometo" }}>
                                Página {page + 1}
                            </Typography>
                            <Button
                                type="button"
                                variant="outlined"
                                disabled={listLoading || lastPage}
                                onClick={() => void loadExpenses(page + 1)}
                                sx={{ fontFamily: "Prometo", textTransform: "none" }}
                            >
                                Próxima
                            </Button>
                        </Stack>
                    </Box>
                </>
            )}

            {expenseView === "form" && (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                            marginBottom: 2,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Tooltip title="Voltar" arrow>
                                <IconButton aria-label="Voltar para o histórico de despesas" onClick={verifyBeforeCancel}>
                                    <ArrowBackOutlined />
                                </IconButton>
                            </Tooltip>
                            <Typography
                                sx={{
                                    fontFamily: "Prometo",
                                    fontWeight: "bold",
                                    fontSize: "17px",
                                }}
                            >
                                {selectedExpense ? "Editar despesa" : "Nova despesa"}
                            </Typography>
                        </Box>

                        {selectedExpense && (
                            <Chip
                                className="crud-metadata-chip"
                                clickable={false}
                                label={`Criado em ${formatDateTime(
                                    selectedExpense.createdAt,
                                )} por ${selectedExpense.userCreated?.trim() || "-"}`}
                                sx={{
                                    color: "black",
                                    fontFamily: "Prometo",
                                    fontWeight: "bolder",
                                }}
                            />
                        )}
                    </Box>

                    <Box className="row tw-m-0" sx={{ margin: 0 }}>
                        <Box className="col-md-3" sx={{ margin: "10px 0" }}>
                            <Autocomplete
                                options={expenseTypes}
                                loading={typesLoading}
                                loadingText="Carregando tipos de despesas..."
                                noOptionsText="Nenhum tipo de despesa encontrado."
                                value={
                                    expenseTypes.find(
                                        (item) => item.id === expense.expenseTypeId,
                                    ) ??
                                    (selectedExpense
                                        ? {
                                            id: selectedExpense.expenseTypeId,
                                            name: selectedExpense.expenseTypeName,
                                            description:
                                                selectedExpense.expenseTypeDescription,
                                        }
                                        : null)
                                }
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                onChange={(_, option) =>
                                    setExpense((previous) => ({
                                        ...previous,
                                        expenseTypeId: option?.id ?? -1,
                                    }))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        required
                                        label="Tipo de despesa"
                                        placeholder="Selecione o tipo de despesa"
                                        InputLabelProps={{
                                            ...params.InputLabelProps,
                                            shrink: true,
                                        }}
                                    />
                                )}
                                disabled={isDisabled || typesLoading}
                            />
                        </Box>

                        <Box className="col-md-3" sx={{ margin: "10px 0" }}>
                            <TextField
                                fullWidth
                                required
                                label="Quantidade"
                                type="number"
                                value={expense.quantity ?? ""}
                                placeholder="Digite a quantidade"
                                InputLabelProps={{ shrink: true }}
                                onChange={(event) =>
                                    setExpense((previous) => ({
                                        ...previous,
                                        quantity:
                                            event.target.value === ""
                                                ? null
                                                : Number(event.target.value),
                                    }))
                                }
                                inputProps={{ min: 0.01, step: "any" }}
                                disabled={isDisabled}
                            />
                        </Box>

                        <Box className="col-md-3" sx={{ margin: "10px 0" }}>
                            <TextField
                                fullWidth
                                required
                                label="Valor total"
                                type="text"
                                value={
                                    expense.totalValue == null
                                        ? ""
                                        : currencyFormatter(
                                            String(
                                                Math.round(
                                                    expense.totalValue * 100,
                                                ),
                                            ),
                                        )
                                }
                                placeholder="Digite o valor total"
                                InputLabelProps={{ shrink: true }}
                                onChange={(event) => {
                                    const numericValue = event.target.value.replace(/\D/g, "");

                                    setExpense((previous) => ({
                                        ...previous,
                                        totalValue:
                                            numericValue === ""
                                                ? null
                                                : Number(numericValue) / 100,
                                    }));
                                }}
                                inputProps={{ inputMode: "numeric" }}
                                disabled={isDisabled}
                            />
                        </Box>

                        <Box className="col-md-3" sx={{ margin: "10px 0" }}>
                            <LocalizationProvider
                                dateAdapter={AdapterDayjs}
                                adapterLocale="pt-br"
                            >
                                <MobileDateTimePicker
                                    sx={{ width: "100%" }}
                                    label="Data da despesa"
                                    format="DD/MM/YYYY HH:mm:ss"
                                    value={
                                        expense.expenseDate
                                            ? dayjs(expense.expenseDate)
                                            : null
                                    }
                                    onChange={(value) =>
                                        setExpense((previous) => ({
                                            ...previous,
                                            expenseDate:
                                                value && dayjs(value).isValid()
                                                    ? dayjs(value).format(
                                                        "YYYY-MM-DDTHH:mm:ss",
                                                    )
                                                    : null,
                                        }))
                                    }
                                    disabled={isDisabled}
                                    slotProps={{
                                        textField: {
                                            required: true,
                                            InputLabelProps: { shrink: true },
                                            placeholder:
                                                "Selecione a data e hora da despesa",
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>

                        <Box className="col-md-12" sx={{ margin: "10px 0" }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Descrição"
                                value={expense.description ?? ""}
                                placeholder="Digite a descrição (Opcional)"
                                InputLabelProps={{ shrink: true }}
                                onChange={(event) =>
                                    setExpense((previous) => ({
                                        ...previous,
                                        description: event.target.value,
                                    }))
                                }
                                inputProps={{ maxLength: 250 }}
                                helperText={`${expense.description?.length ?? 0}/250`}
                                disabled={isDisabled}
                            />
                        </Box>

                        <Box className="col-md-12" sx={{ margin: "10px 0 20px" }}>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: "bolder",
                                    fontFamily: "Prometo",
                                    marginBottom: 1,
                                }}
                            >
                                Anexos (Opcional)
                            </Typography>

                            <input
                                type="file"
                                hidden
                                ref={fileInputRef}
                                accept="image/*"
                                onChange={handleFileSelection}
                            />

                            <Box
                                className="attachment-dropzone"
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "flex-start",
                                    gap: 1,
                                }}
                            >
                                {visibleExistingFiles.map((file, index) =>
                                    renderAttachment(
                                        file.file,
                                        `anexo-${index + 1}`,
                                        file.id,
                                        !isDisabled
                                            ? () =>
                                                setRemovedFiles((previous) =>
                                                    previous.includes(file.id)
                                                        ? previous
                                                        : [...previous, file.id],
                                                )
                                            : undefined,
                                    ),
                                )}

                                {newFiles.map((file, index) =>
                                    renderAttachment(
                                        file.file,
                                        file.name ?? `anexo-${index + 1}`,
                                        file.id,
                                        !isDisabled
                                            ? () =>
                                                setNewFiles((previous) =>
                                                    previous.filter(
                                                        (item) => item.id !== file.id,
                                                    ),
                                                )
                                            : undefined,
                                    ),
                                )}

                                {!isDisabled && attachmentsCount < MAX_ATTACHMENTS && (
                                    <Box
                                        sx={{
                                            minHeight: attachmentsCount > 0 ? 170 : 45,
                                            display: "flex",
                                            alignItems:
                                                attachmentsCount > 0
                                                    ? "center"
                                                    : "flex-start",
                                        }}
                                    >
                                        <Chip
                                            sx={{
                                                color: "#00B700",
                                                "& .MuiChip-icon": { marginLeft: 0 },
                                            }}
                                            label="Anexar"
                                            icon={
                                                <Fab
                                                    size="small"
                                                    aria-label="Adicionar anexo"
                                                    onClick={(event) => {
                                                        event.preventDefault();
                                                        fileInputRef.current?.click();
                                                    }}
                                                    sx={{
                                                        marginRight: 1,
                                                        background: "#00B700",
                                                        color: "#fff",
                                                        "&:hover": {
                                                            background: "#009900",
                                                        },
                                                    }}
                                                >
                                                    <AddOutlined />
                                                </Fab>
                                            }
                                        />
                                    </Box>
                                )}
                            </Box>

                            <Typography
                                variant="caption"
                                sx={{
                                    display: "block",
                                    marginTop: 1,
                                    color: "gray",
                                    fontFamily: "Prometo",
                                }}
                            >
                                Máximo de {MAX_ATTACHMENTS} anexos de imagem.
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        className="tw-flex tw-flex-col-reverse tw-gap-2 tw-border-t tw-border-black/10 tw-pt-4 sm:tw-flex-row sm:tw-justify-end"
                        sx={{ display: "flex", flexDirection: { xs: "column-reverse", sm: "row" }, justifyContent: "flex-end", gap: 1, marginTop: 2, paddingTop: 2, borderTop: "1px solid rgba(0,0,0,0.10)" }}
                    >
                        <Button
                            type="button"
                            onClick={verifyBeforeCancel}
                            disabled={saving}
                            className="btn btn-primary btn-block login-button-close tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            sx={{ color: "#fff", marginRight: 1, fontFamily: "Prometo" }}
                        >
                            <CloseOutlined sx={{ marginRight: 1 }} />
                            Cancelar
                        </Button>

                        <Button
                            type="button"
                            onClick={handleSaveExpense}
                            disabled={isDisabled || saving}
                            className="btn btn-primary btn-block login-button tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            sx={{ color: "#fff", fontFamily: "Prometo" }}
                        >
                            {saving ? (
                                <CircularProgress
                                    size={20}
                                    sx={{ color: "#fff", marginRight: 5 }}
                                />
                            ) : (
                                <SaveOutlined style={{ color: "#fff", marginRight: 5 }} />
                            )}

                            <Typography
                                className="forgot-password"
                                style={{
                                    fontFamily: "Prometo",
                                    fontSize: "16px",
                                    color: "#ffff",
                                }}
                            >
                                {saving
                                    ? selectedExpense
                                        ? "Atualizando..."
                                        : "Salvando..."
                                    : selectedExpense
                                        ? "Atualizar"
                                        : "Salvar"}
                            </Typography>
                        </Button>
                    </Box>
                </>
            )}

            {expenseView === "attachments" && (
                <>
                    <Box sx={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
                        <IconButton title="Voltar" onClick={handleBackToList}>
                            <ArrowBackOutlined />
                        </IconButton>
                        <Typography
                            sx={{
                                fontFamily: "Prometo",
                                fontWeight: "bold",
                                fontSize: "17px",
                            }}
                        >
                            Anexos da despesa
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            minHeight: 220,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            padding: 2,
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            justifyContent: selectedExpense?.files?.length
                                ? "flex-start"
                                : "center",
                            gap: 1,
                        }}
                    >
                        {selectedExpense?.files?.length ? (
                            selectedExpense.files.map((file, index) =>
                                renderAttachment(
                                    file.file,
                                    `anexo-${index + 1}`,
                                    file.id,
                                ),
                            )
                        ) : (
                            <Stack alignItems="center" spacing={1}>
                                <AttachFileOutlined sx={{ fontSize: 40, color: "#6c757d" }} />
                                <Typography sx={{ fontFamily: "Prometo" }}>
                                    Anexos vinculados à despesa nº {selectedExpense?.id ?? "-"}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ fontFamily: "Prometo", color: "gray" }}
                                >
                                    Nenhum anexo encontrado para esta despesa.
                                </Typography>
                            </Stack>
                        )}
                    </Box>
                </>
            )}
        </Paper>
    );
};

export default VehicleExpense;
